import React, {memo, useEffect} from 'react'
import {cloneDeep} from 'lodash'
import {withErrorHandling, withHelmet, withVersionData, withPropsPresent, withServiceData} from '../../../../hoc'
import {CodeEditor} from '../../../shared'
import {Button, Message, Menu} from 'semantic-ui-react'
import OASEditorModal from './oas-editor-modal'
import {useSafeState} from '../../../../hook'
import {updateContract} from '../../../../backend/contract'

const getFromLocalStorage = () => {
  return window.localStorage.getItem('swagger-editor-content')
}

const formatDefinition = (input) => {
  if (isJson(input)) {
    try {
      return JSON.stringify(JSON.parse(input), null, 2)
    } catch {

    }
  }
  return input
}

const isJson = (input) => {
  return input.indexOf('{') === 0
}

const _Contract = (
  {
    service,
    version,
    updateContract
  }
) => {
  const contract = (version.contract || {definition: ''})
  contract.definition = formatDefinition(contract.definition)

  const [state, setState] = useSafeState({
    changed: false,
    original: cloneDeep(contract),
    contract,
    saving: false,
    isOASEditorModalVisible: false
  })

  useEffect(() => {
    setState({
      changed: false,
      original: cloneDeep(contract),
      contract,
      saving: false,
      isOASEditorModalVisible: false
    })
  }, [version.id])

  const onContractChanged = (value) => {
    setState({...state, changed: value !== state.contract.definition, contract: {...state.contract, definition: value}})
  }

  const showOASEditorModal = (e) => {
    e.preventDefault()
    setState({...state, isOASEditorModalVisible: true})
  }

  const hideOASEditorModal = () => {
    setState({...state, isOASEditorModalVisible: false})
  }

  const reset = () => {
    const contract = cloneDeep(state.original)
    setState({...state, original: contract, contract, changed: false})
  }

  const onSave = async (fromLocalStorage = false) => {
    let {contract} = state
    setState({...state, saving: true})
    if (fromLocalStorage) {
      contract = {...contract, ...{definition: getFromLocalStorage()}}
    }
    contract = await updateContract(contract)
    setState({...state, changed: false, isOASEditorModalVisible: false, saving: false, version, contract})
  }

  return (
    <div>
      <div style={{marginBottom: '15px'}}>
        <Menu pointing secondary>
          <Menu.Item
            name='Quick Edit'
            active={!state.isOASEditorModalVisible}
            onClick={hideOASEditorModal}
            data-testid='contract-swagger-link'
          />
          <Menu.Item
            name='Swagger Editor'
            active={state.isOASEditorModalVisible}
            onClick={showOASEditorModal}
          />
          <Menu.Item
            name='Plain Text'
            as='a'
            href={`/api/services/${service.id}/versions/${version.id}/contract/raw`}
            data-testid='contract-raw-link'
          />
        </Menu>
        {
          state.changed && (
            <div>
              <Button size='tiny' basic icon='undo' onClick={() => reset()} content='Undo'
                      data-testid='contract-undo-button'/>
              <Button size='tiny' basic icon='save' primary onClick={() => onSave()} loading={state.saving}
                      data-testid='contract-submit-button' content='Save'/>
            </div>
          )
        }
      </div>
      <div data-testid='contract-editor'>
        <CodeEditor
          cursorStart={-1}
          mode={isJson(state.contract.definition) ? 'json' : 'yaml'}
          theme='xcode'
          height='600px'
          width='100%'
          showPrintMargin={false}
          value={state.contract.definition}
          editorProps={{$blockScrolling: true}}
          onChange={(value) => onContractChanged(value)}
        />
      </div>
      {
        state.isOASEditorModalVisible && (
          <OASEditorModal contract={state.contract} onSave={() => onSave(true)} onClose={() => hideOASEditorModal()}/>
        )
      }
    </div>
  )

}

let Contract = (props) => {
  const {handleError} = props
  props = {...props, updateContract: handleError(updateContract)}
  return (<_Contract {...props}/>)
}

Contract = withPropsPresent(Contract, ['service', 'version'])
Contract = withHelmet(Contract, 'Contract')
Contract = withVersionData(Contract, 'Loading version...')
Contract = withServiceData(Contract, 'Loading service...')
Contract = withErrorHandling(Contract)
Contract = memo(Contract)

export {_Contract, Contract, getFromLocalStorage, formatDefinition}

export default Contract
